/*
 * Shared Plaid relink flow: tries update mode first (preserves data); if Plaid
 * errors out — including the generic "Something went wrong" screen, which exits
 * with error == null — offers a remove-and-fresh-link fallback dialog.
 *
 * Both the Transactions sidebar Reconnect badge and the Settings → Linked Banks
 * Reconnect button funnel through here so the UX stays identical.
 */
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLElement

fun startPlaidRelinkWithFallback(
    itemId: String,
    apiService: ApiService,
    onComplete: (resultItemId: String) -> Unit,
) {
    val helper = PlaidLinkHelper(apiService)
    MainScope().launch {
        // Pre-check: ask Plaid whether the institution is currently healthy enough
        // to bother attempting. If item_logins is DEGRADED/DOWN, warn the user
        // before they go through Plaid Link and watch it fail. Failures here are
        // non-fatal — we proceed to the normal flow on null.
        val status = apiService.getInstitutionStatusByItem(itemId)
        val itemLogins = status?.institution?.status?.item_logins?.status
        val proceed = when (itemLogins) {
            "DOWN" -> confirmInstitutionWarning(
                title = StringKey.INSTITUTION_DOWN_TITLE.tr(status.institution.name),
                body = StringKey.INSTITUTION_DOWN_BODY.tr(status.institution.name),
            )
            "DEGRADED" -> confirmInstitutionWarning(
                title = StringKey.INSTITUTION_DEGRADED_TITLE.tr(status.institution.name),
                body = StringKey.INSTITUTION_DEGRADED_BODY.tr(status.institution.name),
            )
            else -> true // HEALTHY, null, or unknown → no warning
        }
        if (!proceed) return@launch

        loadPlaidLinkScript {
            MainScope().launch {
                helper.initializeRelinkFlow(
                    itemId = itemId,
                    onSuccess = {
                        console.log("Plaid update-mode relink succeeded for $itemId")
                        onComplete(itemId) // update mode preserves the item_id
                    },
                    onError = { msg ->
                        console.warn("Plaid update-mode failed: $msg — offering fresh-link fallback")
                        showFreshLinkFallbackDialog(itemId, apiService, helper, onComplete)
                    },
                )
            }
        }
    }
}

/**
 * Show a "this bank is having issues" warning modal. Returns true if the user
 * elected to proceed anyway, false if they chose to wait.
 */
private suspend fun confirmInstitutionWarning(title: String, body: String): Boolean =
    kotlinx.coroutines.suspendCancellableCoroutine { cont ->
        val theme = WebTheme.current
        if (document.getElementById("institution-status-overlay") != null) {
            cont.resume(true) {} // already showing — fall through
            return@suspendCancellableCoroutine
        }

        val overlay = document.createElement("div") as HTMLElement
        overlay.id = "institution-status-overlay"
        overlay.style.cssText = """
            position: fixed; inset: 0;
            background: rgba(0,0,0,0.6);
            display: flex; align-items: center; justify-content: center;
            z-index: 10000;
        """

        val dialog = document.createElement("div") as HTMLElement
        dialog.style.cssText = """
            background: ${theme.card};
            border: 1px solid ${theme.border};
            border-radius: 12px;
            padding: 24px;
            min-width: 320px;
            max-width: 440px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.4);
        """

        val titleEl = document.createElement("div") as HTMLElement
        titleEl.textContent = title
        titleEl.style.cssText = "font-size: 16px; font-weight: 600; color: ${theme.expenseRed}; margin-bottom: 8px;"
        dialog.appendChild(titleEl)

        val bodyEl = document.createElement("div") as HTMLElement
        bodyEl.textContent = body
        bodyEl.style.cssText = "font-size: 14px; color: ${theme.textSecondary}; margin-bottom: 20px; line-height: 1.5;"
        dialog.appendChild(bodyEl)

        val btnRow = document.createElement("div") as HTMLElement
        btnRow.style.cssText = "display: flex; justify-content: flex-end; gap: 8px;"

        val waitBtn = document.createElement("button") as HTMLElement
        waitBtn.textContent = StringKey.INSTITUTION_WAIT.tr()
        waitBtn.style.cssText = """
            background: ${theme.accent};
            border: none;
            color: ${if (theme.isDark) "#000000" else "#FFFFFF"};
            font-size: 14px; font-weight: 600;
            padding: 8px 16px; border-radius: 8px;
            cursor: pointer;
        """
        waitBtn.addEventListener("click", {
            overlay.remove()
            cont.resume(false) {}
        })

        val tryAnywayBtn = document.createElement("button") as HTMLElement
        tryAnywayBtn.textContent = StringKey.INSTITUTION_TRY_ANYWAY.tr()
        tryAnywayBtn.style.cssText = """
            background: transparent;
            border: 1px solid ${theme.border};
            color: ${theme.textSecondary};
            font-size: 14px; font-weight: 500;
            padding: 8px 16px; border-radius: 8px;
            cursor: pointer;
        """
        tryAnywayBtn.addEventListener("click", {
            overlay.remove()
            cont.resume(true) {}
        })

        btnRow.appendChild(tryAnywayBtn)
        btnRow.appendChild(waitBtn)
        dialog.appendChild(btnRow)
        overlay.appendChild(dialog)
        document.body?.appendChild(overlay)
    }

private fun showFreshLinkFallbackDialog(
    itemId: String,
    apiService: ApiService,
    helper: PlaidLinkHelper,
    onComplete: (resultItemId: String) -> Unit,
) {
    if (document.getElementById("relink-fallback-overlay") != null) return
    val theme = WebTheme.current

    val overlay = document.createElement("div") as HTMLElement
    overlay.id = "relink-fallback-overlay"
    overlay.style.cssText = """
        position: fixed; inset: 0;
        background: rgba(0,0,0,0.6);
        display: flex; align-items: center; justify-content: center;
        z-index: 10000;
    """

    val dialog = document.createElement("div") as HTMLElement
    dialog.style.cssText = """
        background: ${theme.card};
        border: 1px solid ${theme.border};
        border-radius: 12px;
        padding: 24px;
        min-width: 320px;
        max-width: 440px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.4);
    """

    val title = document.createElement("div") as HTMLElement
    title.textContent = StringKey.RELINK_FALLBACK_TITLE.tr()
    title.style.cssText = "font-size: 16px; font-weight: 600; color: ${theme.textPrimary}; margin-bottom: 8px;"
    dialog.appendChild(title)

    val body = document.createElement("div") as HTMLElement
    body.textContent = StringKey.RELINK_FALLBACK_BODY.tr()
    body.style.cssText = "font-size: 14px; color: ${theme.textSecondary}; margin-bottom: 20px; line-height: 1.5;"
    dialog.appendChild(body)

    val btnRow = document.createElement("div") as HTMLElement
    btnRow.style.cssText = "display: flex; justify-content: flex-end; gap: 8px;"

    val cancelBtn = document.createElement("button") as HTMLElement
    cancelBtn.textContent = StringKey.RELINK_FALLBACK_CANCEL.tr()
    cancelBtn.style.cssText = """
        background: transparent;
        border: 1px solid ${theme.border};
        color: ${theme.textSecondary};
        font-size: 14px; font-weight: 500;
        padding: 8px 16px; border-radius: 8px;
        cursor: pointer;
    """
    cancelBtn.addEventListener("click", { overlay.remove() })
    btnRow.appendChild(cancelBtn)

    val confirmBtn = document.createElement("button") as HTMLElement
    confirmBtn.textContent = StringKey.RELINK_FALLBACK_PROCEED.tr()
    confirmBtn.style.cssText = """
        background: ${theme.accent};
        border: none;
        color: ${if (theme.isDark) "#000000" else "#FFFFFF"};
        font-size: 14px; font-weight: 600;
        padding: 8px 16px; border-radius: 8px;
        cursor: pointer;
    """
    confirmBtn.addEventListener("click", {
        overlay.remove()
        removeAndFreshLink(itemId, apiService, helper, onComplete)
    })
    btnRow.appendChild(confirmBtn)

    dialog.appendChild(btnRow)
    overlay.addEventListener("click", { e ->
        if (e.target == overlay) overlay.remove()
    })
    overlay.appendChild(dialog)
    document.body?.appendChild(overlay)
}

private fun removeAndFreshLink(
    itemId: String,
    apiService: ApiService,
    helper: PlaidLinkHelper,
    onComplete: (resultItemId: String) -> Unit,
) {
    GlobalScope.launch {
        try {
            console.log("Revoking item $itemId before fresh link")
            apiService.unlinkInstitution(itemId)
        } catch (e: Exception) {
            // If revoke fails we still try a fresh link — the new item replaces the old anyway.
            console.warn("Revoke failed (will continue with fresh link): ${e.message}")
        }
        loadPlaidLinkScript {
            MainScope().launch {
                helper.initializePlaidLink(
                    daysRequested = null,
                    onSuccess = { newItemId ->
                        console.log("Fresh link succeeded after fallback (new item: $newItemId)")
                        onComplete(newItemId)
                    },
                    onError = { msg -> console.warn("Fresh link failed: $msg") },
                )
            }
        }
    }
}
